/** 通话记录 Tab — 列出 sip_call_log, 自动从 SIP 服务订阅写库 */
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloseOutlined,
  DeleteOutlined,
  PhoneOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  Button,
  Empty,
  List,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useEffect, useState } from 'react';
import { type CallLogEntry, sipPhoneApi } from '../../api/sip-phone-api.js';

interface Props {
  onDial: (number: string, name?: string) => void;
  refreshKey: number;
}

function fmtDate(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
}

function fmtDuration(sec: number | null) {
  if (!sec) return '00:00';
  const mm = String(Math.floor(sec / 60)).padStart(2, '0');
  const ss = String(sec % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

function statusTag(status: CallLogEntry['status']) {
  const map: Record<CallLogEntry['status'], { color: string; label: string }> = {
    completed: { color: 'green', label: '已接通' },
    missed: { color: 'red', label: '未接' },
    failed: { color: 'red', label: '失败' },
    cancelled: { color: 'default', label: '已取消' },
    'in-progress': { color: 'blue', label: '通话中' },
  };
  const v = map[status];
  return (
    <Tag color={v.color} style={{ fontSize: 10 }}>
      {v.label}
    </Tag>
  );
}

export function CallLogTab({ onDial, refreshKey }: Props) {
  const [items, setItems] = useState<CallLogEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const reload = () => {
    setLoading(true);
    sipPhoneApi
      .listCalls(50, 0)
      .then(setItems)
      .catch(() => message.error('加载通话记录失败'))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [refreshKey]);

  const handleDelete = async (id: string) => {
    try {
      await sipPhoneApi.deleteCall(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch {
      message.error('删除失败');
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 8 }}>
        <Typography.Text strong>通话记录</Typography.Text>
        <Button size="small" type="text" icon={<ReloadOutlined />} onClick={reload}>
          刷新
        </Button>
      </Space>

      <Spin spinning={loading}>
        {items.length === 0 ? (
          <Empty description="暂无通话记录" />
        ) : (
          <List
            size="small"
            dataSource={items}
            style={{ maxHeight: 420, overflowY: 'auto' }}
            renderItem={(it) => (
              <List.Item
                style={{ padding: '6px 4px' }}
                actions={[
                  <Tooltip title="再次拨打" key="call">
                    <Button
                      type="text"
                      size="small"
                      icon={<PhoneOutlined style={{ color: '#1677ff' }} />}
                      onClick={() => onDial(it.remoteNumber, it.remoteName ?? undefined)}
                    />
                  </Tooltip>,
                  <Popconfirm
                    key="del"
                    title="删除这条记录?"
                    onConfirm={() => handleDelete(it.id)}
                    okText="删除"
                    cancelText="取消"
                  >
                    <Button type="text" size="small" icon={<DeleteOutlined />} danger />
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    it.direction === 'outbound' ? (
                      <ArrowUpOutlined
                        style={{ color: it.status === 'completed' ? '#52c41a' : '#999' }}
                      />
                    ) : it.status === 'missed' ? (
                      <CloseOutlined style={{ color: '#ff4d4f' }} />
                    ) : (
                      <ArrowDownOutlined style={{ color: '#1677ff' }} />
                    )
                  }
                  title={
                    <Space size={4}>
                      <Typography.Text style={{ fontSize: 13 }}>
                        {it.remoteName ?? it.remoteNumber}
                      </Typography.Text>
                      {statusTag(it.status)}
                    </Space>
                  }
                  description={
                    <Space size={6}>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        {fmtDate(it.startAt)}
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        {fmtDuration(it.durationSec)}
                      </Typography.Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Spin>
    </div>
  );
}
